/**
 * UC2 — Positive change and scaling opportunities (client spec
 * `2026-10-01-UC2_LAUNCH_SPEC.md` §2/§3, and the 2 Oct chart round's design §2).
 *
 * Above the strip: the level switch (watershed | country) and the seven exclusive chips
 * with their legend — the map controls, which belong to every tab. Under it, five
 * sub-tabs (`#uc2Tabs`), one panel mounted at a time: Overview (the headline tiles, the
 * per-unit chart that follows the chip, and what this use case reads), Trends (the
 * clicked unit's cropland, rainfall and vegetation charts), Practices (the group selector
 * and the adoption chart), Similar (the three most alike and the side-by-side), Method
 * (the eight sentences). A tab that needs a click says so rather than rendering empty.
 *
 * **Selection is a map click only.** `useUnitLink('uc2')` adopts whatever the reader
 * clicks (and resolves a region/commune click to the watershed under it), and the
 * selected unit is outlined on every chip, so flipping chips shows the same place through
 * different evidence.
 *
 * **One request on open, one per click** (client §0.2): `uc2/summary` and
 * `uc2/units?level=…` fill the chips, every choropleth, the tiles and the units chart;
 * `uc2/units/{id}` fills the per-unit charts, the strip and the captions. The similar
 * block is precomputed per practice group on that same payload, so the `#uc2Practice`
 * selector adds no request either.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  fetchConfig,
  fetchLayers,
  fetchUnitDetail,
  queryKeys,
  type IntelUc2SummaryBlocks,
} from '../../lib/api';
import { fmtCompactNum, fmtNum } from '../../lib/format';
import { useAppStore, WATERSHED_LEVEL } from '../../lib/state';
import { CompareUnits } from './CompareUnits';
import { IntelFocus } from './IntelFocus';
import { Methodology } from './Methodology';
import { SimilarStrip } from './SimilarStrip';
import { BarSeries, type BarSeriesBar } from './charts/BarSeries';
import { StatTile, StatTiles } from './charts/StatTile';
import { SubPanel, SubTabs } from './SubTabs';
import { Uc2Card } from './Uc2Card';
import { Uc2Chips } from './Uc2Chips';
import { Uc2Contrasts } from './Uc2Contrasts';
import { Uc2Drivers } from './Uc2Drivers';
import { Uc2FocusTiles } from './Uc2FocusTiles';
import { Uc2Indicators } from './Uc2Indicators';
import { Uc2Practices } from './Uc2Practices';
import { Uc2Scaling } from './Uc2Scaling';
import { Uc2Trends } from './Uc2Trends';
import { Uc2UnitsChart } from './Uc2UnitsChart';
import { useIntelSummary, useIntelUnitDetail, useIntelUnits } from './api';
import { chipPaint } from './uc2Paint';
import {
  PROGRAMME_BOUNDS,
  STABILITY_BAND_PCT,
  UC2_GREENING_LAYER,
  UC2_LC_CHANGE_LAYER,
  UC2_PRACTICE_GROUPS,
  UC2_PROGRAMME_SCOPE,
  UC2_SCOPE_ISO3,
  UC2_TABS,
  UC2_WATERSHED_ONLY_CHIPS,
  normaliseChip,
  normalisePractice,
  uc2Scope,
  type Uc2Level,
  type Uc2Tab,
} from './uc2Constants';
import { useRasterSlot } from './useRasterSlot';
import { useUnitLink } from './useUnitLink';

/** `uc2/summary`'s own body (backend task-1 report §4.1). Every field optional: the live
 * run is still the pre-launch one until the recompute lands, and a missing field reads as
 * "nothing to show" rather than as a crash. */
interface Uc2SummaryBody extends IntelUc2SummaryBlocks {
  by_category?: Record<string, Record<string, number>>;
  /**
   * The band the run actually classified with, in percent (backend §5). Read rather than
   * mirrored as a frontend constant, so a curator's `PATCH uc2/config` moves the map's
   * cropland steps and the card's captions together with the engine.
   */
  stability_band?: number;
  positive_area_ha?: number;
  top_practice?: string | null;
  n_opportunities?: number;
  programme_means?: Record<string, number | null>;
  greening?: { completeness?: number | null; gated?: boolean; threshold?: number | null };
}

export function Uc2() {
  const { t } = useTranslation();
  const [level, setLevel] = useState<Uc2Level>('watershed');
  /**
   * `#uc2Scope` — which country the three analytic tabs read at country level while the
   * reader has clicked no country of their own. Local, like the level switch: it chooses
   * what a panel reads, not what the map is painted from. `null` until the reader picks
   * one, which is then the first scope the run serves.
   */
  const [countryScope, setCountryScope] = useState<string | null>(null);
  const addResultLayer = useAppStore((s) => s.addResultLayer);
  const removeResultLayer = useAppStore((s) => s.removeResultLayer);
  const setReportingLevel = useAppStore((s) => s.setReportingLevel);
  const fitBounds = useAppStore((s) => s.fitBounds);
  const compareWith = useAppStore((s) => s.intel.compareWith);
  const setIntelCompareWith = useAppStore((s) => s.setIntelCompareWith);
  const storedChip = useAppStore((s) => s.intel.uc2Chip);
  const storedPractice = useAppStore((s) => s.intel.uc2Practice);
  const storedSubTab = useAppStore((s) => s.intel.subTab);
  const role = useAppStore((s) => s.role);
  const setIntelUc2Chip = useAppStore((s) => s.setIntelUc2Chip);
  const setIntelUc2Practice = useAppStore((s) => s.setIntelUc2Practice);
  const chip = normaliseChip(storedChip);
  const practice = normalisePractice(storedPractice);
  const subTab: Uc2Tab = UC2_TABS.includes(storedSubTab as Uc2Tab)
    ? (storedSubTab as Uc2Tab)
    : UC2_TABS[0];

  const summary = useIntelSummary('uc2');
  // The rows of whichever level the switch is on: the choropleth, the legend counts and
  // the units chart are all one pass over them (client §0.2's one request on open).
  const units = useIntelUnits('uc2', level);
  /**
   * The watershed rows, whatever level the switch is on: at country level the three
   * evidence chips derive their share from them (design §2.1), and this is the very
   * `uc2/units?level=watershed` the pane fetched on open — one cache entry, no request.
   */
  const watershedUnits = useIntelUnits('uc2', 'watershed');
  const { unit, pick, clear } = useUnitLink('uc2');
  const detail = useIntelUnitDetail('uc2', unit);

  // The selected unit's geometry, for the land-cover chip's own fit. Asked for only while
  // that chip is open, and shared with every other reader of the same cache entry.
  const unitBounds = useQuery({
    queryKey: queryKeys.unit(unit ?? ''),
    queryFn: () => fetchUnitDetail(unit as string),
    enabled: Boolean(unit) && chip === 'landcover',
  });

  /**
   * The catalogue and the §G ramps, for the two raster chips' legends: neither paints a
   * choropleth of its own, so its key has to be the published COG's own, read exactly as
   * `widgets/Legend.tsx` reads any switched-on raster. Both are the same cached GETs the
   * sidebar already has in flight, so this costs no request (client §0.2).
   */
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const greeningLayer = useMemo(
    () => (layers ?? []).find((l) => l.id === UC2_GREENING_LAYER) ?? null,
    [layers],
  );
  const lcChangeLayer = useMemo(
    () => (layers ?? []).find((l) => l.id === UC2_LC_CHANGE_LAYER) ?? null,
    [layers],
  );

  const rows = useMemo(() => units.data ?? [], [units.data]);
  const watershedRows = useMemo(() => watershedUnits.data ?? [], [watershedUnits.data]);
  /**
   * The focused unit's own row at the level the pane is on (`#intelFocus`, or a map
   * click). A unit focused at the other level has none here, and the headline tiles then
   * stay on the programme rather than printing a half-empty unit block.
   */
  const focusedRow = rows.find((r) => r.unit_id === unit);
  const body = (summary.data?.summary ?? {}) as Uc2SummaryBody;
  const byWatershed = body.by_category?.watershed ?? {};
  const band =
    typeof body.stability_band === 'number' && Number.isFinite(body.stability_band)
      ? body.stability_band
      : STABILITY_BAND_PCT;
  const greeningOpen = body.greening?.gated === false;
  const isCountryLevel = level === 'admin0';
  /** The countries this run actually carries a slice for, in its own order. */
  const scopeOptions = useMemo(
    () => Object.keys(body.by_class_series ?? {}).filter((k) => k !== UC2_PROGRAMME_SCOPE),
    [body.by_class_series],
  );

  /**
   * The similar block the `similar` chip, the strip and the units chart all read: the
   * selected watershed's own precomputed top-3 for the selected practice group. A detail
   * from the pre-launch run carries none, which paints the chip neutral rather than
   * throwing.
   */
  const similar = useMemo(
    () => detail.data?.similar?.[practice] ?? [],
    [detail.data, practice],
  );

  const paint = useMemo(
    () =>
      chipPaint({
        rows,
        chip,
        practice,
        level,
        selected: unit,
        greeningLayer,
        lcChangeLayer,
        ramps: config?.ramps,
        band,
        watershedRows,
        label: (k, o) => t(k, o ?? {}),
      }),
    [
      rows,
      chip,
      practice,
      level,
      unit,
      greeningLayer,
      lcChangeLayer,
      config?.ramps,
      band,
      watershedRows,
      t,
    ],
  );

  /**
   * A `units` result layer is painted from the explorer's own polygons
   * (`features/map/resultUnits.ts`), so the pane has to state the level it has scores for
   * — exactly the UC1 fix (NOTES #729): at the app's default level the choropleth would
   * match no feature and the map would stay blank while the chips and the tiles filled in.
   */
  useEffect(() => {
    setReportingLevel(isCountryLevel ? 0 : WATERSHED_LEVEL);
  }, [isCountryLevel, setReportingLevel]);

  /**
   * Both raster chips are watershed readings and nothing else. Switching to country level
   * with one of them pressed falls back to Pattern, the one chip that has a country
   * number, rather than leaving a pressed chip whose evidence has just been greyed out.
   */
  useEffect(() => {
    if (isCountryLevel && UC2_WATERSHED_ONLY_CHIPS.includes(chip)) setIntelUc2Chip('pattern');
  }, [isCountryLevel, chip, setIntelUc2Chip]);

  /**
   * The choropleth, redrawn in place (same id) whenever the chip, the level, the practice
   * or the selection changes. Not drawn at all on the two raster chips: there the evidence
   * *is* the published COG, and a filled choropleth over it would hide the very thing the
   * chip exists to show.
   */
  useEffect(() => {
    if (!rows.length || chip === 'landcover' || (chip === 'greening' && greeningOpen)) {
      removeResultLayer('intel:uc2:choropleth');
      return;
    }
    addResultLayer(
      {
        id: 'intel:uc2:choropleth',
        kind: 'units',
        label: t(`intel.uc2.chip.${chip}`),
        unit_ids: rows.map((r) => r.unit_id),
        colors: paint.colors,
        patterns: paint.patterns,
        details: paint.details,
        outlines: paint.outlines,
        classes: paint.classes,
      },
      { jobId: `uc2-${level}-${chip}-${practice}`, tool: 'uc2' },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, chip, level, practice, paint, greeningOpen]);

  /**
   * Land-cover change (2 Oct round, design §2.4): the precompute's own categorical COG,
   * drawn the way the greening chip draws its trend raster — the swipe between the two
   * annual land-cover maps is retired, because reading a change class off two maps under
   * a handle is work the raster has already done. The store's `swipePair` is untouched
   * and still serves every other tool.
   *
   * Greening: the same slot, greyed until the archive completes and live the moment the
   * run says so (client §6.4, config not redeploy).
   */
  useRasterSlot(UC2_LC_CHANGE_LAYER, chip === 'landcover');
  useRasterSlot(UC2_GREENING_LAYER, chip === 'greening' && greeningOpen);

  /**
   * The watershed boundaries, while a raster chip is drawn (client round 2 Oct 2026).
   *
   * A raster chip takes the choropleth off the map on purpose — a filled unit over the
   * raster would hide the pixels the chip exists to show — and until this round that left
   * the map with **nothing to click**: the §F5.8 rows underneath are `fill: false`, and
   * NOTES #731 keeps them outline-only while a use case is open. So a reader's click over
   * the land-cover raster did nothing, on the one chip that is *about* where change
   * happened and the one a reader is most likely to click.
   *
   * What replaces it is one transparent, interactive `units` layer carrying **every**
   * watershed at the current level: no fill, a hairline boundary, nothing else. It is a
   * hit target and a set of boundaries, so the raster stays the evidence on screen.
   *
   * The selected watershed's own thick outline is **not** drawn here. `useUnitLink` draws
   * it on every chip, for every use case, as `intel:{uc}:selected` — and since the same
   * client round that layer is an outline rather than the solid blue fill it used to be,
   * which is what makes it readable over a raster at all. Drawing it twice would be two
   * layers owning one statement.
   */
  useEffect(() => {
    const rasterChip = chip === 'landcover' || (chip === 'greening' && greeningOpen);
    if (!rasterChip || !rows.length) {
      removeResultLayer('intel:uc2:boundaries');
      return;
    }
    addResultLayer(
      {
        id: 'intel:uc2:boundaries',
        kind: 'units',
        label: t('intel.uc2.legend.boundaries'),
        unit_ids: rows.map((r) => r.unit_id),
        outlineOnly: true,
        hairline: true,
      },
      { jobId: `uc2-boundaries-${level}`, tool: 'uc2' },
    );
    return () => removeResultLayer('intel:uc2:boundaries');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chip, greeningOpen, rows, level]);

  /**
   * "…opened directly on the selected watershed" (client §2). Its own effect, so that
   * clicking another watershed while the chip is open moves the view to it without tearing
   * the raster down and rebuilding it. The bounds come from the same one-row
   * `/hierarchy/units/{id}` the map's own fits read (`useUnitLink`), so a unit the map has
   * already looked at costs no request; with nothing selected the chip opens on the
   * programme.
   */
  useEffect(() => {
    if (chip !== 'landcover') return;
    if (!unit) {
      fitBounds(PROGRAMME_BOUNDS);
      return;
    }
    const bbox = unitBounds.data?.bbox;
    if (!bbox) return;
    const [minx, miny, maxx, maxy] = bbox;
    fitBounds([
      [miny, minx],
      [maxy, maxx],
    ]);
    // `dataUpdatedAt` as well as the payload: a box that arrives after another fit has
    // already moved the map (the result layer's own, a country change) is re-asserted
    // rather than lost, so the chip really does open on the watershed it was asked for.
  }, [chip, unit, unitBounds.data, unitBounds.dataUpdatedAt, fitBounds]);

  /**
   * `#uc2Practice`. It belongs to **two** places — the Practices tab, above the chart it
   * drives, and under the chips while the practices chip is painting the map from another
   * tab. Exactly **one of the two is in the DOM at a time** (fix wave H): two identical
   * selects a scroll apart, one of them inert-looking because the other carries the id, is
   * a reader asking which of them is the real one. Both write the same store field, so
   * they cannot disagree.
   */
  const practiceSelect = (testId: string) => (
    <label className="intel-unitpick" data-testid={testId}>
      {t('intel.uc2.practiceLabel')}{' '}
      <select
        id="uc2Practice"
        aria-label={t('intel.uc2.practiceLabel')}
        value={practice}
        onChange={(e) => setIntelUc2Practice(e.target.value)}
      >
        {UC2_PRACTICE_GROUPS.map((g) => (
          <option key={g} value={g}>
            {t(`intel.uc2.practice.${g}`)}
          </option>
        ))}
      </select>
    </label>
  );
  // Exactly one `#uc2Practice` in the DOM (fix wave H): the Practices tab and the Similar
  // tab each carry their own, so the chip only takes the selector when neither is showing.
  const chipOwnsPractice =
    chip === 'practices' && subTab !== 'practices' && subTab !== 'similar';
  const practiceLabel = t(`intel.uc2.practice.${practice}`);
  const practiceLower = t(`intel.uc2.practiceLower.${practice}`);
  const programmeMean = body.programme_means?.[practice] ?? null;
  /**
   * One watershed's adoption of the selected group, from the `uc2/units?level=watershed`
   * rows the pane already holds — the Similar tab's two-bar cards draw the figures the
   * engine stored only the *difference* between. Watershed rows whatever the level switch
   * says, because a pair is two watersheds even while the map is painted by country.
   */
  const adoptionOf = (unitId: string): number | null => {
    const value = watershedRows.find((r) => r.unit_id === unitId)?.adoption?.[practice];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  };
  /**
   * And one watershed's class, from the same rows — the Most-alike cards need it for the
   * same reason: a candidate the run calls positive must draw in the positive green there
   * too, or the colour means one thing in one block of the tab and nothing in the other.
   */
  const categoryOf = (unitId: string): string | null =>
    watershedRows.find((r) => r.unit_id === unitId)?.category ?? null;
  const isCountryUnit = detail.data?.unit.level === 'admin0';
  const watershedSelected = Boolean(detail.data) && !isCountryUnit;
  /**
   * The clicked unit when it is a country. The **detail** says so — an id that looks like
   * a country code is not proof that it is one, and the run's scopes are its own business.
   */
  const selectedCountry = isCountryUnit ? (detail.data?.unit.id ?? null) : null;

  const topPractice = body.top_practice
    ? t(`intel.uc2.practice.${body.top_practice}`, { defaultValue: body.top_practice })
    : null;

  /**
   * The headline tiles at country level. Only the first has a country meaning the run
   * actually counts — `by_category.admin0`, countries by class — and it is used when the
   * run serves it. The other three are programme figures whatever the switch says (one
   * area, one most-recorded practice, one count of opportunities across the programme),
   * so at country level they say "programme" in the label rather than letting a reader
   * who has just switched to Country read them as that country's own.
   */
  const byCountry = body.by_category?.admin0 ?? {};
  const countriesPositive = typeof byCountry.positive === 'number' ? byCountry.positive : null;
  const tileLabel = (key: string) =>
    t(isCountryLevel ? `intel.uc2.tile.${key}Programme` : `intel.uc2.tile.${key}`);

  /** The pick hint a tab prints where the clicked watershed's own block would stand. */
  const pickHint = (
    <p id="intelPickHint" className="note">
      {t('intel.uc2.pickHint')}
    </p>
  );

  /**
   * Which slice of the precomputed blocks the three analytic tabs read (2 Oct tab round):
   * the programme at watershed level, and at country level the country the reader clicked
   * — or, with none clicked, the one `#uc2Scope` holds. The select is in the DOM only in
   * that last case: with a country selected, the map click *is* the scope, and a second
   * control disagreeing with it would be the two-selects problem again.
   *
   * The countries on offer are the run's own (`by_class_series`' keys), never a frontend
   * list: a run that gains or loses one needs no release here, and a country the run has
   * no slice for makes the panels print their note rather than another country's numbers
   * under its name.
   */
  const scope = uc2Scope(level, selectedCountry, countryScope, scopeOptions);
  const scopeSelect =
    isCountryLevel && !selectedCountry && scopeOptions.length ? (
      <label className="intel-unitpick" data-testid="uc2-scope">
        {t('intel.uc2.scopeLabel')}{' '}
        <select
          id="uc2Scope"
          aria-label={t('intel.uc2.scopeLabel')}
          value={scope}
          onChange={(e) => setCountryScope(e.target.value)}
        >
          {scopeOptions.map((code) => (
            <option key={code} value={code}>
              {t(`country.${UC2_SCOPE_ISO3[code] ?? code}`, { defaultValue: code })}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  const vegBars: BarSeriesBar[] = (detail.data?.series?.veg ?? []).map((p) => ({
    id: String(p.year),
    label: String(p.year),
    value: typeof p.pct === 'number' ? p.pct : null,
  }));

  return (
    <div className="intel-uc2">
      {summary.data?.stale ? (
        <div id="intelConfigNote" className="note">
          {t('intel.configNote')}
        </div>
      ) : null}

      {/* One card for everything that says what the map is showing (layout round, 2 Oct
          2026): the level switch, the chips, their legend, and the practice sub-selector
          the practices chip owns. */}
      <div className="intel-card intel-mapcontrols">
        <label className="intel-level">
          <span>{t('intel.uc1.levelLabel')}</span>
          <select
            id="intelLevel"
            value={level}
            onChange={(e) => setLevel(e.target.value as Uc2Level)}
          >
            <option value="watershed">{t('intel.uc1.levelWatershed')}</option>
            <option value="admin0">{t('intel.uc1.levelCountry')}</option>
          </select>
        </label>
        <IntelFocus
          rows={rows}
          level={level}
          selected={unit}
          classLabel={(row) => t(`intel.uc2.category.${row.category}`, { defaultValue: row.category })}
          onFocus={(id, name) => void pick(id, name)}
          onClear={clear}
        />
        <Uc2Chips
          classes={paint.classes}
          note={paint.note}
          greeningOpen={greeningOpen}
          countryLevel={isCountryLevel}
          practiceSelect={chipOwnsPractice ? practiceSelect('uc2-practice-chip') : undefined}
        />
      </div>

      <SubTabs prefix="uc2" tabs={UC2_TABS} />

      {subTab === 'overview' ? (
        <SubPanel prefix="uc2" name="overview">
          {focusedRow ? (
            <Uc2FocusTiles
              row={focusedRow}
              detail={detail.data ?? null}
              name={focusedRow.name}
              practice={practice}
              onBack={clear}
            />
          ) : summary.data ? (
            <div id="intelHeadlines" className="intel-headlines intel-card">
              <StatTiles>
                <StatTile
                  id="uc2-positive"
                  value={fmtNum(
                    isCountryLevel && countriesPositive != null
                      ? countriesPositive
                      : (byWatershed.positive ?? 0),
                    0,
                  )}
                  label={
                    isCountryLevel && countriesPositive != null
                      ? t('intel.uc2.tile.positiveCountries')
                      : tileLabel('positive')
                  }
                />
                <StatTile
                  id="uc2-positive-area"
                  value={fmtCompactNum(body.positive_area_ha ?? 0)}
                  unit="ha"
                  label={tileLabel('area')}
                />
                <StatTile
                  id="uc2-top-practice"
                  value={topPractice ?? '—'}
                  text
                  label={tileLabel('topPractice')}
                />
                <StatTile
                  id="uc2-opportunities"
                  value={fmtNum(body.n_opportunities ?? 0, 0)}
                  label={tileLabel('opportunities')}
                />
              </StatTiles>
            </div>
          ) : null}
          <Uc2UnitsChart
            rows={rows}
            chip={chip}
            level={level}
            practice={practice}
            programmeMean={body.programme_means?.[practice] ?? null}
            band={band}
            watershedRows={watershedRows}
            selectedId={unit}
          />
          {/* What the pattern chip's verdict is made of, under the chart that paints it
              (client round 3 Oct 2026). Counted rather than averaged at country level. */}
          <Uc2Drivers
            rows={rows}
            level={level}
            focusedRow={focusedRow}
            detail={detail.data ?? null}
            band={band}
            selectedId={unit}
          />
          <Uc2Indicators />
        </SubPanel>
      ) : null}

      {subTab === 'trends' ? (
        <SubPanel prefix="uc2" name="trends">
          {/* A panel's own selectors are a card like every other block (layout round,
              2 Oct 2026) — loose on the panel ground they read as a stray row. */}
          {scopeSelect ? <div className="intel-card intel-panelcontrols">{scopeSelect}</div> : null}
          {/* The same block at the top of Trends: it is the shortest reading of the
              period, and the series below are the long one. */}
          <Uc2Drivers
            rows={rows}
            level={level}
            focusedRow={focusedRow}
            detail={detail.data ?? null}
            band={band}
            selectedId={unit}
          />
          <Uc2Trends
            byClass={body.by_class_series?.[scope] ?? undefined}
            greeningByUnit={body.greening_by_unit}
            scope={scope}
            band={band}
            detail={detail.data ?? null}
            level={level}
            units={rows}
            byClassSeries={body.by_class_series}
            selectedId={unit}
          />
          {detail.data ? (
            <Uc2Card
              detail={detail.data}
              practice={practice}
              band={band}
              practiceSelect={null}
              only={['cropland', 'rain']}
              after={
                vegBars.length ? (
                  <div id="uc2Trend-greening" className="uc2-chart">
                    <h6 className="intel-block-title">{t('intel.uc2.chart.greening')}</h6>
                    <BarSeries
                      bars={vegBars}
                      unit="%"
                      ariaLabel={t('intel.uc2.chart.greening')}
                      testId="uc2-bars-greening"
                    />
                  </div>
                ) : null
              }
            />
          ) : (
            pickHint
          )}
        </SubPanel>
      ) : null}

      {subTab === 'practices' ? (
        <SubPanel prefix="uc2" name="practices">
          <div className="intel-card intel-panelcontrols">
            {scopeSelect}
            {practiceSelect('uc2-practice-tab')}
          </div>
          <Uc2Practices
            adoption={body.adoption_by_class?.[scope] ?? undefined}
            detail={detail.data ?? null}
            nPositive={body.by_class_series?.[scope]?.n?.positive ?? null}
          />
          {detail.data ? (
            <Uc2Card
              detail={detail.data}
              practice={practice}
              band={band}
              practiceSelect={null}
              only={['practices']}
            />
          ) : (
            pickHint
          )}
        </SubPanel>
      ) : null}

      {subTab === 'similar' ? (
        <SubPanel prefix="uc2" name="similar">
          <div className="intel-card intel-panelcontrols">
            {scopeSelect}
            {/* The selector first, then the one sentence saying what a "pair" is — without
                it a reader takes two alike watersheds for a before-and-after of one place. */}
            {practiceSelect('uc2-practice-similar')}
            <p className="intel-card-sub" data-testid="uc2-pairs-help">
              {t('intel.uc2.contrastsHelp')}
            </p>
          </div>
          {watershedSelected ? (
            <>
              <SimilarStrip
                reference={{
                  id: detail.data!.unit.id,
                  name: detail.data!.unit.name,
                  category: detail.data!.category,
                  adoption: adoptionOf(detail.data!.unit.id),
                }}
                rows={similar}
                categoryOf={categoryOf}
                practiceLabel={practiceLabel}
                practiceLower={practiceLower}
                programmeMean={programmeMean}
                compareWith={compareWith}
                onCompare={(id) => setIntelCompareWith(compareWith === id ? null : id)}
              />
              {compareWith ? (
                <CompareUnits
                  useCase="uc2"
                  a={detail.data!.unit.id}
                  b={compareWith}
                  practice={practice}
                  onClose={() => setIntelCompareWith(null)}
                />
              ) : null}
            </>
          ) : (
            pickHint
          )}
          {/* Where a practice would go first, above the pairs it is derived from. */}
          <Uc2Scaling
            candidates={body.scaling_candidates}
            scope={scope}
            focusedId={unit}
            practiceLabel={(group) =>
              t(`intel.uc2.practice.${group}`, { defaultValue: group })
            }
          />
          <Uc2Contrasts
            pairs={body.contrast_pairs}
            scope={scope}
            practice={practice}
            practiceLabel={practiceLabel}
            practiceLower={practiceLower}
            adoptionOf={adoptionOf}
            programmeMean={programmeMean}
          />
        </SubPanel>
      ) : null}

      {subTab === 'method' ? (
        <SubPanel prefix="uc2" name="method">
          <Methodology useCase="uc2" open />
        </SubPanel>
      ) : null}
    </div>
  );
}

export default Uc2;
